import { useQuery } from '@apollo/client';
import React from 'react';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { EntityWithNotes } from '@atlassian/cssxp-types-audit';
import { ENABLE_INVOICE_VIEW_PAYMENT_RETRY } from '@atlassian/cssxp-utils-generic';
import { AllTasksList } from '@css-xp/apd-task-manager';
import { StatelessInvoiceLineItems as InvoiceLineItems } from '../../../account-subscriptions/billing-v2/invoices/src/components/invoice-line-items';
import {
  getInvoiceAdditionalDetails,
  getInvoiceHeader,
  getInvoiceSubheader,
  InvoiceWithLineItemsPPC,
} from '../../../account-subscriptions/billing-v2/invoices/src/invoice-page-utils';
import { PageHeader, SummarySectionV2 } from '../../application';
import { AuditNotesV2 } from '../../audit-notes';
import { BillingDocDetailsV2 } from '../../billing-docs-v2';
import { ErrorPanel, GenericErrorPage, NotFoundError } from '../../error-page';
import { withApollo } from '../../graphql-provider';
import { useFeatureToggle } from '../../hooks';
import { PaymentRetryV2 } from '../../payment-retry';
import './invoice-preview-v2.scss';
import { fetchInvoiceForSidebarQuery } from './queries/fetch-invoice-query';

interface Props {
  invoiceId: string;
}

const InvoicePreviewV2 = ({ invoiceId: id }: Props) => {
  const [isEnabled] = useFeatureToggle();
  const { data, loading, error } = useQuery(fetchInvoiceForSidebarQuery, {
    variables: {
      id,
    },
    fetchPolicy: 'no-cache',
  });

  if (loading) return <LoadingIndicator />;
  if (error) return <GenericErrorPage error={error} />;
  if (!data) {
    return <ErrorPanel error={new NotFoundError(`Unable to find invoice with ID: ${id}`)} />;
  }

  const invoice = data?.invoice as InvoiceWithLineItemsPPC;
  const auditTrailProps = {
    entityName: EntityWithNotes.INVOICE,
    id: invoice?.id,
  };

  return (
    <div className="invoice-preview-v2-container" data-testid="invoice-preview-v2">
      {invoice && (
        <>
          <PageHeader icon={<div />} header={getInvoiceHeader(invoice)} subheader={getInvoiceSubheader(invoice)} />
          <SummarySectionV2.SummarySection
            testId="summary"
            className="summary-section-wrapper"
            columns="repeat(auto-fit, minmax(100px, 1fr))"
            styles={{ marginTop: '0' }}
          >
            <BillingDocDetailsV2 additionalContent={getInvoiceAdditionalDetails(invoice)} billingDoc={invoice} />
          </SummarySectionV2.SummarySection>
          <InvoiceLineItems invoice={invoice} />
          {auditTrailProps && <AuditNotesV2 {...auditTrailProps} />}
          <AllTasksList businessKey={invoice.invoiceNumber ?? undefined} searchable={false} />
          {invoice?.dunningStatus && isEnabled(ENABLE_INVOICE_VIEW_PAYMENT_RETRY) && (
            <PaymentRetryV2 dunningInfo={invoice?.dunningStatus} />
          )}
        </>
      )}
    </div>
  );
};

export default withApollo(InvoicePreviewV2);
